/**
 * MissionEventModal – globales Popup für Einbruchs-Mission-Events.
 *
 * Erscheint tab-übergreifend über allen Inhalten.
 * Zeigt DECISION- und REACTION-Events mit Optionen und zwei Countdown-Phasen:
 * (1) "Nächstes Event in..." — Cooldown bevor die Entscheidung überhaupt offen ist,
 * (2) "Zeit für diese Entscheidung..." — Entscheidungsfenster, danach AFK-Auto-Resolve.
 * PASSIVE-Events erscheinen hier nie (lösen sich serverseitig sofort auf, siehe crimeOps.ts).
 * Design: Terminal-UI, Border-Akzent je Event-Typ, Uppercase Labels.
 */

export type MissionEventType = 'PASSIVE' | 'REACTION' | 'DECISION';

// Optionen die der Spieler sieht
export interface MissionEventOption {
  index: number;
  label: string;
  outcome: 'CONTINUE' | 'ABORT' | 'BONUS' | 'RISK';
  riskDelta: number;
  rewardMod: number;
  requires: { itemType: string; qty: number; available: boolean } | null;
}

// Nächstes Event einer Mission
export interface MissionEvent {
  id: string;
  type: MissionEventType;
  label: string;
  description: string;
  timerSeconds: number;
  decisionExpiresAt: string | null;
  choices: MissionEventOption[];
}

export interface MissionEventModalOptions {
  missionId: string;
  contactName: string;
  missionType: string;
  event: MissionEvent;
  nextEventAvailableAt: string | null;
  onDecide: (missionId: string, eventId: string, optionIndex: number) => Promise<void>;
  onClose: () => void;
}

let activeModal: HTMLElement | null = null;
let countdownInterval: ReturnType<typeof setInterval> | null = null;
let decisionCountdownInterval: ReturnType<typeof setInterval> | null = null;

function clearModal(): void {
  if (countdownInterval) { clearInterval(countdownInterval); countdownInterval = null; }
  if (decisionCountdownInterval) { clearInterval(decisionCountdownInterval); decisionCountdownInterval = null; }
  if (activeModal) { activeModal.remove(); activeModal = null; }
}

function outcomeColor(outcome: MissionEventOption['outcome']): string {
  switch (outcome) {
    case 'ABORT':    return 'var(--accent-red)';
    case 'BONUS':    return 'var(--accent-green)';
    case 'RISK':     return 'var(--accent-orange)';
    case 'CONTINUE': return 'var(--text-primary)';
  }
}

function riskLabel(riskDelta: number): string {
  if (riskDelta > 0.1) return `RISIKO +${(riskDelta * 100).toFixed(0)}%`;
  if (riskDelta < -0.1) return `RISIKO ${(riskDelta * 100).toFixed(0)}%`;
  return '';
}

function esc(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function injectModalStyles(): void {
  const styleId = 'mission-event-modal-styles';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    /* Shell = .modal-backdrop/.modal aus dashboard.css (siehe components/BottomSheet.ts).
       .me-modal ist die Themenvariante fuer dieses Modal, .me-modal--decision/--reaction
       varianieren nur die Randfarbe (gleiche Tokens wie dashboard.ts's Job-Event-Popup:
       event-type-badge--decision/--reaction), kein zweites Modal-Skelett. */
    .me-modal { border-color: var(--accent-gold); }
    .me-modal--decision { border-color: var(--accent-orange); }
    .me-modal--reaction { border-color: var(--accent-red); }
    .me-modal .modal-header { border-bottom-color: var(--accent-gold); }
    .me-modal .modal-header h2 { color: var(--accent-gold); }
    .me-header-badge-row { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
    .event-type-badge {
      display: inline-block;
      font-size: 10px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      padding: 0.15rem 0.4rem;
      border: 1px solid var(--border);
      color: var(--text-muted);
    }
    .event-type-badge--decision { border-color: var(--accent-orange); color: var(--accent-orange); }
    .event-type-badge--reaction { border-color: var(--accent-red); color: var(--accent-red); }
    .me-contact {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      margin-bottom: 14px;
      letter-spacing: 0.06em;
    }
    .me-event-label {
      font-size: var(--font-size-sm);
      color: var(--text-primary);
      text-transform: uppercase;
      letter-spacing: 0.08em;
      margin-bottom: 8px;
    }
    .me-description {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      font-style: italic;
      margin-bottom: 16px;
      line-height: 1.5;
    }
    .me-options { display: flex; flex-direction: column; gap: 8px; }
    .me-option {
      display: flex; flex-direction: column;
      border: 1px solid var(--border);
      padding: 10px 12px;
      cursor: pointer;
      background: var(--bg-elevated);
      transition: border-color 0.1s;
      min-height: 44px;
    }
    .me-option:hover:not(:disabled) { border-color: var(--text-primary); }
    .me-option:disabled { opacity: 0.35; cursor: not-allowed; }
    .me-option-label {
      font-size: var(--font-size-xs);
      letter-spacing: 0.1em;
      text-transform: uppercase;
    }
    .me-option-meta {
      font-size: 0.75rem;
      color: var(--text-muted);
      margin-top: 3px;
    }
    .me-option-req {
      font-size: 0.72rem;
      margin-top: 2px;
    }
    .me-req-ok   { color: var(--accent-green); }
    .me-req-fail { color: var(--accent-red); }
    .me-reaction-btn {
      display: block; width: 100%;
      font-family: var(--font-mono);
      font-size: var(--font-size-lg);
      letter-spacing: 0.1em;
      text-transform: uppercase;
      padding: 18px 12px;
      border: 2px solid var(--accent-red);
      background: var(--bg-elevated);
      color: var(--accent-red);
      cursor: pointer;
      transition: background 0.1s;
    }
    .me-reaction-btn:hover:not(:disabled) { background: var(--accent-red); color: var(--bg-base); }
    .me-reaction-btn:disabled { opacity: 0.35; cursor: not-allowed; }
    .me-reaction-caption {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      text-align: center;
      margin-top: 8px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }
    .me-cooldown, .me-decision-timer {
      margin-top: 16px;
      padding-top: 12px;
      border-top: 1px solid var(--border);
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      text-align: center;
    }
    .me-cooldown-timer {
      font-size: var(--font-size-sm);
      color: var(--accent-gold);
      letter-spacing: 0.08em;
      margin-top: 4px;
    }
    .me-decision-timer-value {
      font-size: var(--font-size-sm);
      color: var(--accent-orange);
      letter-spacing: 0.08em;
      margin-top: 4px;
    }
    .me-close-hint {
      margin-top: 12px;
      font-size: 0.72rem;
      color: var(--text-muted);
      text-align: center;
      letter-spacing: 0.06em;
    }
    .me-close-btn {
      display: block; width: 100%;
      font-family: var(--font-mono); font-size: var(--font-size-xs);
      letter-spacing: 0.1em; padding: 8px;
      border: 1px solid var(--border); background: transparent;
      color: var(--text-muted); cursor: pointer; text-transform: uppercase;
    }
    .me-close-btn:hover { border-color: var(--text-primary); color: var(--text-primary); }
  `;
  document.head.appendChild(style);
}

export function showMissionEventModal(opts: MissionEventModalOptions): void {
  clearModal();
  injectModalStyles();

  const { missionId, contactName, event, nextEventAvailableAt, onDecide, onClose } = opts;

  const isCooldownActive = nextEventAvailableAt
    ? new Date(nextEventAvailableAt).getTime() > Date.now()
    : false;

  const isReaction = event.type === 'REACTION';
  const typeModifierClass = isReaction ? 'me-modal--reaction' : 'me-modal--decision';
  const badgeClass = isReaction ? 'event-type-badge--reaction' : 'event-type-badge--decision';
  const badgeLabel = isReaction ? 'REAKTION' : 'ENTSCHEIDUNG';

  const optionsHtml = isReaction
    ? (() => {
        const choice = event.choices[0];
        if (!choice) return '';
        const isDisabled = isCooldownActive ? 'disabled' : '';
        return `
          <button class="me-reaction-btn" data-index="0" ${isDisabled}>JETZT REAGIEREN!</button>
          <div class="me-reaction-caption">${esc(choice.label)}</div>
        `;
      })()
    : `
      <div class="me-options">
        ${event.choices.map((opt) => {
          const color = outcomeColor(opt.outcome);
          const risk = riskLabel(opt.riskDelta);
          const hasRequirement = opt.requires !== null;
          const requirementMet = opt.requires?.available ?? true;
          const isDisabled = isCooldownActive || (hasRequirement && !requirementMet) ? 'disabled' : '';
          const reqHtml = opt.requires
            ? `<span class="me-option-req ${opt.requires.available ? 'me-req-ok' : 'me-req-fail'}">
                ${opt.requires.available ? '✓' : '✗'} BENÖTIGT: ${esc(opt.requires.itemType)} × ${opt.requires.qty}
               </span>`
            : '';
          return `
            <button class="me-option" data-index="${opt.index}" ${isDisabled} style="color:${color}">
              <span class="me-option-label">${esc(opt.label)}</span>
              ${risk ? `<span class="me-option-meta">${esc(risk)}</span>` : ''}
              ${reqHtml}
            </button>
          `;
        }).join('')}
      </div>
    `;

  const overlay = document.createElement('div');
  overlay.id = 'mission-event-overlay';
  overlay.className = 'modal-backdrop modal-backdrop--elevated';
  overlay.innerHTML = `
    <div class="modal me-modal ${typeModifierClass}" role="dialog" aria-modal="true" aria-label="Einbruch-Mission">
      <div class="modal-header">
        <div class="me-header-badge-row">
          <span class="event-type-badge ${badgeClass}">${badgeLabel}</span>
        </div>
        <h2>EINBRUCH-MISSION</h2>
      </div>
      <div class="modal-body">
        <div class="me-contact">KONTAKT: ${esc(contactName)}</div>
        <div class="me-event-label">${esc(event.label)}</div>
        <div class="me-description">${esc(event.description)}</div>
        ${optionsHtml}
        ${!isCooldownActive && event.decisionExpiresAt ? `
          <div class="me-decision-timer">
            ZEIT FÜR DIESE ENTSCHEIDUNG
            <div class="me-decision-timer-value" id="me-decision-countdown">--:--</div>
          </div>
        ` : ''}
        ${isCooldownActive && nextEventAvailableAt ? `
          <div class="me-cooldown">
            NÄCHSTES EVENT IN
            <div class="me-cooldown-timer" id="me-countdown">--:--</div>
          </div>
        ` : ''}
      </div>
      <div class="modal-actions">
        <button class="me-close-btn" id="me-close">SCHLIESSEN</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  activeModal = overlay;

  // Countdown-Timer wenn Cooldown aktiv (Entscheidung noch nicht offen)
  if (isCooldownActive && nextEventAvailableAt) {
    const target = new Date(nextEventAvailableAt).getTime();
    const countdownEl = overlay.querySelector<HTMLElement>('#me-countdown');

    countdownInterval = setInterval(() => {
      const remaining = Math.max(0, target - Date.now());
      if (remaining <= 0) {
        if (countdownInterval) { clearInterval(countdownInterval); countdownInterval = null; }
        // Cooldown abgelaufen: Modal neu rendern mit aktivierten Buttons
        showMissionEventModal({ ...opts, nextEventAvailableAt: null });
        return;
      }
      const mins = Math.floor(remaining / 60_000);
      const secs = Math.floor((remaining % 60_000) / 1_000);
      if (countdownEl) {
        countdownEl.textContent = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      }
    }, 1_000);
  }

  // Countdown-Timer für das offene Entscheidungsfenster (danach AFK-Auto-Resolve serverseitig)
  if (!isCooldownActive && event.decisionExpiresAt) {
    const target = new Date(event.decisionExpiresAt).getTime();
    const countdownEl = overlay.querySelector<HTMLElement>('#me-decision-countdown');

    decisionCountdownInterval = setInterval(() => {
      const remaining = Math.max(0, target - Date.now());
      const mins = Math.floor(remaining / 60_000);
      const secs = Math.floor((remaining % 60_000) / 1_000);
      if (countdownEl) {
        countdownEl.textContent = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      }
      if (remaining <= 0 && decisionCountdownInterval) {
        clearInterval(decisionCountdownInterval);
        decisionCountdownInterval = null;
      }
    }, 1_000);
  }

  // Options-/Reaktions-Klicks
  overlay.querySelectorAll<HTMLButtonElement>('.me-option:not(:disabled), .me-reaction-btn:not(:disabled)').forEach((btn) => {
    btn.addEventListener('click', () => {
      const index = parseInt(btn.dataset.index ?? '0', 10);
      // Buttons während der API-Anfrage sperren
      overlay.querySelectorAll<HTMLButtonElement>('.me-option, .me-reaction-btn').forEach((b) => { b.disabled = true; });
      void (async () => {
        try {
          await onDecide(missionId, event.id, index);
        } finally {
          clearModal();
          onClose();
        }
      })();
    });
  });

  // Schließen-Button
  overlay.querySelector('#me-close')?.addEventListener('click', () => {
    clearModal();
    onClose();
  });
}

export function hideMissionEventModal(): void {
  clearModal();
}
